import { useCallback, useEffect, useState } from "react";
import { Gauge, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import "@/components/accuracy/BandTuning.css";

// Auto-tuning of the opponent-difficulty band factors from graded snapshots.
// Read-only on the Accuracy page; with an Apply button when `onApply` is provided (Settings).
export function BandTuning({ onApply, refreshKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/rotation/band-tuning").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (data === null) return <p className="bt-dim" data-testid="band-tuning-loading">Υπολογισμός…</p>;
  if (data === false) return null;

  const suggestions = data.bands.filter((b) => b.has_suggestion);
  const graded = data.bands.some((b) => b.n > 0);

  const apply = async () => {
    if (!onApply || !suggestions.length) return;
    setBusy(true);
    try { await onApply(data.suggested_factors); load(); } finally { setBusy(false); }
  };

  return (
    <section className="bt-root" data-testid="band-tuning">
      <div className="bt-head">
        <Gauge size={14} />
        <h3>Auto-tuning συντελεστών (band A–F)</h3>
      </div>
      <p className="bt-note">
        Πρόταση διόρθωσης όταν ≥ {data.min_games} αγώνες στη band & μέσο bias ≥ {data.min_bias} πόντοι/ομάδα
        (bias = προβλεπόμενοι − πραγματικοί πόντοι· θετικό = υπερεκτίμηση → χαμηλότερος συντελεστής).
      </p>
      {!graded ? (
        <p className="bt-empty" data-testid="band-tuning-empty">Δεν υπάρχουν ακόμη βαθμολογημένοι αγώνες ανά band.</p>
      ) : (
        <>
          <div className="bt-table-scroll">
            <table className="bt-table" data-testid="band-tuning-table">
              <thead><tr><th>Band</th><th className="r">Αγώνες</th><th className="r">Bias</th><th className="r">MAE</th><th className="r">Τώρα</th><th className="r">Πρόταση</th></tr></thead>
              <tbody>
                {data.bands.map((b) => (
                  <tr key={b.band} className={b.has_suggestion ? "bt-sug" : ""} data-testid={`band-tuning-row-${b.band}`}>
                    <td>{b.band} · {b.label}</td>
                    <td className="r">{b.n}</td>
                    <td className={`r ${b.bias == null ? "" : b.bias > 0 ? "bt-pos" : b.bias < 0 ? "bt-neg" : ""}`}>{b.bias == null ? "—" : `${b.bias > 0 ? "+" : ""}${b.bias}`}</td>
                    <td className="r">{b.mae == null ? "—" : b.mae}</td>
                    <td className="r">{b.current}</td>
                    <td className="r">{b.has_suggestion ? <b className="bt-sug-val" data-testid={`band-tuning-sug-${b.band}`}>{b.suggested}</b> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {onApply && (
            <button type="button" className="bt-apply" onClick={apply} disabled={busy || !suggestions.length} data-testid="band-tuning-apply">
              <Wand2 size={14} /> {suggestions.length ? `Εφαρμογή προτάσεων (${suggestions.length})` : "Καμία πρόταση"}
            </button>
          )}
          {!onApply && suggestions.length > 0 && (
            <p className="bt-hint" data-testid="band-tuning-hint">
              Προτεινόμενες αλλαγές σε {suggestions.length} band — εφαρμόζονται από το Settings (κάρτα συντελεστών).
            </p>
          )}
        </>
      )}
    </section>
  );
}
